/**
 * Auto-resize utility functions for textarea and input elements
 * Provides consistent auto-resizing behavior across the application
 */

/**
 * Auto-resize a textarea element based on content
 * @param {HTMLTextAreaElement} element - The textarea element to resize
 * @param {Object} options - Configuration options
 * @param {number} options.minHeight - Minimum height in pixels (default: 30)
 * @param {number} options.maxHeight - Maximum height in pixels (default: 120)
 * @param {number} options.lineHeight - Line height in pixels for calculation (default: 20)
 */
export const autoResizeTextarea = (element, options = {}) => {
  const { minHeight = 30, maxHeight = 120, lineHeight = 20 } = options;

  if (!element || element.tagName !== 'TEXTAREA') {
    console.warn('autoResizeTextarea: Element must be a textarea');
    return;
  }

  // Store the original height for calculation
  const originalHeight = element.style.height;

  // Reset height to auto to get accurate scrollHeight
  element.style.height = 'auto';

  // Calculate the content height
  const scrollHeight = element.scrollHeight;

  // Ensure we respect min and max height constraints
  const newHeight = Math.max(minHeight, Math.min(scrollHeight, maxHeight));

  // Set the new height
  element.style.height = newHeight + 'px';

  // Handle overflow for content that exceeds max height
  if (scrollHeight > maxHeight) {
    element.style.overflowY = 'auto';
  } else {
    element.style.overflowY = 'hidden';
  }

  return newHeight;
};

/**
 * Create an auto-resize handler function for textarea elements
 * @param {Object} options - Configuration options (same as autoResizeTextarea)
 * @returns {Function} Event handler function
 */
export const createAutoResizeHandler = (options = {}) => {
  return (event) => {
    autoResizeTextarea(event.target, options);
  };
};

/**
 * Enhanced auto-resize with smooth transitions
 * @param {HTMLTextAreaElement} element - The textarea element to resize
 * @param {Object} options - Configuration options
 */
export const autoResizeTextareaSmooth = (element, options = {}) => {
  const {
    minHeight = 30,
    maxHeight = 120,
    transitionDuration = '0.2s',
  } = options;

  if (!element || element.tagName !== 'TEXTAREA') {
    console.warn('autoResizeTextareaSmooth: Element must be a textarea');
    return;
  }

  // Add transition if not already present
  if (!element.style.transition.includes('height')) {
    element.style.transition =
      `height ${transitionDuration} ease, ${element.style.transition}`.replace(
        /^, /,
        ''
      );
  }

  // Use the base auto-resize function
  return autoResizeTextarea(element, options);
};

/**
 * Initialize auto-resize on a textarea element
 * @param {HTMLTextAreaElement} element - The textarea element
 * @param {Object} options - Configuration options
 * @returns {Function} Cleanup function to remove event listeners
 */
export const initAutoResize = (element, options = {}) => {
  if (!element || element.tagName !== 'TEXTAREA') {
    console.warn('initAutoResize: Element must be a textarea');
    return () => {};
  }

  const handler = createAutoResizeHandler(options);

  // Add event listeners
  element.addEventListener('input', handler);
  element.addEventListener('paste', handler);
  element.addEventListener('cut', handler);

  // Initial resize
  setTimeout(() => autoResizeTextarea(element, options), 0);

  // Return cleanup function
  return () => {
    element.removeEventListener('input', handler);
    element.removeEventListener('paste', handler);
    element.removeEventListener('cut', handler);
  };
};

/**
 * React hook for auto-resize functionality
 * @param {Object} options - Configuration options
 * @returns {Function} Ref callback function for React elements
 */
export const useAutoResize = (options = {}) => {
  let cleanup = null;

  const refCallback = (element) => {
    // Clean up previous instance
    if (cleanup) {
      cleanup();
      cleanup = null;
    }

    // Initialize new instance
    if (element) {
      cleanup = initAutoResize(element, options);
    }
  };

  // Return the ref callback and cleanup for manual use
  return { ref: refCallback, cleanup: () => cleanup && cleanup() };
};

export default {
  autoResizeTextarea,
  createAutoResizeHandler,
  autoResizeTextareaSmooth,
  initAutoResize,
  useAutoResize,
};
